import { i18n } from "../i18n";

const t = (key: string) => i18n.global.t(key);

export default function getWeatherDescription(code: number | string): string {
  // Accept both numeric and string inputs
  const n = typeof code === 'string' ? parseInt(code, 10) : code;

  /* ---------- 1. OpenWeatherMap codes ---------- */
  const owmMap: Record<number, () => string> = {
    // Thunderstorm
    200: () => t("utils.get_weather_description.thunderstorm_with_light_rain"),
    201: () => t("utils.get_weather_description.thunderstorm_with_rain"),
    202: () => t("utils.get_weather_description.thunderstorm_with_heavy_rain"),
    210: () => t("utils.get_weather_description.light_thunderstorm"),
    211: () => t("utils.get_weather_description.thunderstorm"),
    212: () => t("utils.get_weather_description.heavy_thunderstorm"),
    221: () => t("utils.get_weather_description.ragged_thunderstorm"),
    230: () => t("utils.get_weather_description.thunderstorm_with_light_drizzle"),
    231: () => t("utils.get_weather_description.thunderstorm_with_drizzle"),
    232: () => t("utils.get_weather_description.thunderstorm_with_heavy_drizzle"),

    // Drizzle
    300: () => t("utils.get_weather_description.light_drizzle"),
    301: () => t("utils.get_weather_description.drizzle"),
    302: () => t("utils.get_weather_description.heavy_drizzle"),
    310: () => t("utils.get_weather_description.light_drizzle_rain"),
    311: () => t("utils.get_weather_description.drizzle_rain"),
    312: () => t("utils.get_weather_description.heavy_drizzle_rain"),
    313: () => t("utils.get_weather_description.shower_drizzle"),
    314: () => t("utils.get_weather_description.heavy_shower_drizzle"),
    321: () => t("utils.get_weather_description.shower_drizzle"),

    // Rain
    500: () => t("utils.get_weather_description.light_rain"),
    501: () => t("utils.get_weather_description.moderate_rain"),
    502: () => t("utils.get_weather_description.heavy_rain"),
    503: () => t("utils.get_weather_description.very_heavy_rain"),
    504: () => t("utils.get_weather_description.extreme_rain"),
    511: () => t("utils.get_weather_description.freezing_rain"),
    520: () => t("utils.get_weather_description.light_shower_rain"),
    521: () => t("utils.get_weather_description.shower_rain"),
    522: () => t("utils.get_weather_description.heavy_shower_rain"),
    531: () => t("utils.get_weather_description.ragged_shower_rain"),

    // Snow
    600: () => t("utils.get_weather_description.light_snow"),
    601: () => t("utils.get_weather_description.snow"),
    602: () => t("utils.get_weather_description.heavy_snow"),
    611: () => t("utils.get_weather_description.sleet"),
    612: () => t("utils.get_weather_description.light_shower_sleet"),
    613: () => t("utils.get_weather_description.shower_sleet"),
    615: () => t("utils.get_weather_description.light_rain_and_snow"),
    616: () => t("utils.get_weather_description.rain_and_snow"),
    620: () => t("utils.get_weather_description.light_shower_snow"),
    621: () => t("utils.get_weather_description.shower_snow"),
    622: () => t("utils.get_weather_description.heavy_shower_snow"),

    // Atmosphere
    701: () => t("utils.get_weather_description.mist"),
    711: () => t("utils.get_weather_description.smoke"),
    721: () => t("utils.get_weather_description.haze"),
    731: () => t("utils.get_weather_description.dust_whirls"),
    741: () => t("utils.get_weather_description.fog"),
    751: () => t("utils.get_weather_description.sand"),
    761: () => t("utils.get_weather_description.dust"),
    762: () => t("utils.get_weather_description.volcanic_ash"),
    771: () => t("utils.get_weather_description.squalls"),
    781: () => t("utils.get_weather_description.tornado"),

    // Clear / clouds
    800: () => t("utils.get_weather_description.clear_sky"),
    801: () => t("utils.get_weather_description.few_clouds"),
    802: () => t("utils.get_weather_description.scattered_clouds"),
    803: () => t("utils.get_weather_description.broken_clouds"),
    804: () => t("utils.get_weather_description.overcast_clouds"),
  };

  /* ---------- 2. WMO codes (what you had) ---------- */
  const wmoMap: Record<number, () => string> = {
    0: () => t("utils.get_weather_description.clear_sky"),
    1: () => t("utils.get_weather_description.mainly_clear"),
    2: () => t("utils.get_weather_description.partly_cloudy"),
    3: () => t("utils.get_weather_description.overcast"),
    45: () => t("utils.get_weather_description.fog"),
    48: () => t("utils.get_weather_description.depositing_rime_fog"),
    51: () => t("utils.get_weather_description.light_drizzle"),
    53: () => t("utils.get_weather_description.moderate_drizzle"),
    55: () => t("utils.get_weather_description.dense_drizzle"),
    56: () => t("utils.get_weather_description.light_freezing_drizzle"),
    57: () => t("utils.get_weather_description.dense_freezing_drizzle"),
    61: () => t("utils.get_weather_description.slight_rain"),
    63: () => t("utils.get_weather_description.moderate_rain"),
    65: () => t("utils.get_weather_description.heavy_rain"),
    66: () => t("utils.get_weather_description.light_freezing_rain"),
    67: () => t("utils.get_weather_description.heavy_freezing_rain"),
    71: () => t("utils.get_weather_description.slight_snow"),
    73: () => t("utils.get_weather_description.moderate_snow"),
    75: () => t("utils.get_weather_description.heavy_snow"),
    77: () => t("utils.get_weather_description.snow_grains"),
    80: () => t("utils.get_weather_description.slight_rain_showers"),
    81: () => t("utils.get_weather_description.moderate_rain_showers"),
    82: () => t("utils.get_weather_description.violent_rain_showers"),
    85: () => t("utils.get_weather_description.slight_snow_showers"),
    86: () => t("utils.get_weather_description.heavy_snow_showers"),
    95: () => t("utils.get_weather_description.thunderstorm_slight_moderate"),
    96: () => t("utils.get_weather_description.thunderstorm_with_slight_hail"),
    99: () => t("utils.get_weather_description.thunderstorm_with_heavy_hail"),
  };

  const label = owmMap[n] ?? wmoMap[n];
  return label ? label() : t("utils.get_weather_description.unknown_weather_condition");
}
